<script lang="ts">
    import { onMounted, Ref, ref } from 'vue';
    import vm, { saveCurrentJourney, exitToStartMenu } from '@journeys/Journeys.viewmodel';

    // components
    import FluidButton from '@shared/components/FluidButton.vue';
    import { requestFrame } from '@utils';

    // assets
    import {
        SaveSVG,
        CircularArrowSVG,
        SettingsSVG,
        NodesSVG,
        SaveTemplateSVG,
        GlobeAltSVG, // GlobeSVG,
        DoubleArrowsLeftSVG,
        DoubleArrowsRightSVG,
    } from '@shared/assets/vector/ui/IconsUI';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'UI_Sidebar',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const { settings, viewport, editor, nav, canvas } = vm;

    onMounted(() => {});

    let temptimer: NodeJS.Timeout;

    const showPropertyWindow = (state?: boolean) => {
        let propertyWindowSize = settings.ui.propertyWindowSize * settings.ui.rem;

        requestFrame(() => {
            editor.state.showPropertyWindow = typeof state === 'undefined' ? !editor.state.showPropertyWindow : state;
            viewport.dims.w -= editor.state.showPropertyWindow ? propertyWindowSize : -propertyWindowSize;
            nav.x += editor.state.showPropertyWindow ? propertyWindowSize / 2 : -propertyWindowSize / 2;
        });

        // let nw = bitshift.round(-nav.z * viewport.pmw.value) + 5
        // if (nav.x < nw) nav.x = nw
        // if (nav.x + viewport.dims.w + nw > canvas.w.value) nav.x = canvas.w.value - viewport.dims.w - nw

        // tempfix
        document.body.classList.add('prevent-scrollbar-opaque');
        clearTimeout(temptimer);
        temptimer = setTimeout(() => document.body.classList.remove('prevent-scrollbar-opaque'), 700);
    };

    editor.events.addListener('showpropertywindow', showPropertyWindow);

    if (editor.state.showPropertyWindow && !nav.x)
        nav.x += editor.state.showPropertyWindow
            ? (settings.ui.propertyWindowSize * settings.ui.rem) / 2
            : (-settings.ui.propertyWindowSize * settings.ui.rem) / 2;

    const handleAction = (action: string) => {
        switch (action) {
            case 'savecurrent':
                saveCurrentJourney();
                break;
            case 'open':
                exitToStartMenu();
                break;
        }
    };

    const openMenu = (menu: string) => {
        if (!editor.state.showNodeMenu && editor.state.activeMenu === menu)
            editor.state.showPropertyWindow ? showPropertyWindow(false) : showPropertyWindow(true);
        else if (!editor.state.showPropertyWindow) showPropertyWindow(true);

        if (editor.state.showNodeMenu) editor.state.showNodeMenu = false;

        editor.state.activeMenu = menu;
    };
</script>

<template>
    <div class="container-sidebar">
        <FluidButton :text="$Resources.SaveButton" :icon="SaveSVG" @click="() => handleAction('savecurrent')" class="btn btn-primary" />

        <div class="d-flex flex-even">
            <FluidButton
                style="border-right: 1px solid var(--border-color)"
                :text="$Resources.UndoBtn"
                textClasses="sr-only"
                class="btn btn-subtle"
                :icon="CircularArrowSVG"
                @click="() => editor.events.emit('historyundo')" />

            <FluidButton
                :text="$Resources.RedoBtn"
                textClasses="sr-only"
                class="btn btn-subtle"
                style="transform: scale(-1, 1)"
                :icon="CircularArrowSVG"
                @click="() => editor.events.emit('historyredo')" />
        </div>

        <FluidButton
            aria-controls="settings"
            role="tab"
            :text="$Resources.SettingsTab"
            :icon="SettingsSVG"
            @click="() => openMenu('settings')"
            :class="editor.state.activeMenu === 'settings' && 'active'"
            class="btn btn-subtle" />

        <FluidButton
            aria-controls="nodes"
            role="tab"
            :text="$Resources.NodesLabel"
            :icon="NodesSVG"
            @click="() => openMenu('nodelists')"
            :class="editor.state.activeMenu === 'nodelists' && 'active'"
            class="btn btn-subtle" />

        <FluidButton
            aria-controls="templates"
            role="tab"
            :text="$Resources.SaveAsTemplateButton"
            :icon="SaveTemplateSVG"
            @click="() => openMenu('savetemplate')"
            :class="editor.state.activeMenu === 'savetemplate' && 'active'"
            class="btn btn-subtle" />

        <FluidButton
            aria-controls="publish"
            role="tab"
            :text="$Resources.CronScheduleOption"
            :icon="GlobeAltSVG"
            @click="() => openMenu('publish')"
            :class="editor.state.activeMenu === 'publish' ? 'btn-subtle active' : 'btn-cta'"
            class="btn" />

        <FluidButton
            :icon="editor.state.showPropertyWindow ? DoubleArrowsRightSVG : DoubleArrowsLeftSVG"
            class="btn btn-subtle"
            @click="() => showPropertyWindow()" />

        <FluidButton
            v-if="editor.dev.show.admin_tools"
            text="dev"
            :class="['btn btn-subtle', editor.state.activeMenu === 'developer' ? 'active' : '']"
            @click="() => openMenu('developer')"
            style="grid-row: 11"
            textstyle="font-size: .8rem" />
    </div>
</template>

<style lang="less">
    .container-sidebar {
        --container-sidebar-height: var(--editor-edit-pane-nav-width);
        --container-sidebar-width: var(--editor-edit-pane-nav-width);
        display: grid;
        position: relative;
        grid-template-rows:
            calc(var(--container-sidebar-height) + 0.2rem) 1.6rem repeat(7, var(--container-sidebar-height))
            auto var(--container-sidebar-height);
        height: 100%;
        width: var(--container-sidebar-width);
        background-color: var(--bg-color);
        border-left: 1px solid var(--border-color);
        z-index: 4;
    }

    .container-sidebar .btn {
        border-width: 0;
        border-radius: 0;
        padding: 0.3rem;
        white-space: normal;
        letter-spacing: normal;

        &.active {
            // margin: 0 -1px;
            border-width: 1px 0; // 2px;
            border-color: var(--border-color);
            // border-left-color: var(--bg-color);
            // border-right-color: var(--bg-color);

            &:after {
                content: '';
                inset: 0;
                background: var(--bg-color);
                position: absolute;
                margin-left: -2px;
                width: 4px;
            }
        }
    }

    .container-sidebar .button-wrapper {
        flex-direction: column;

        > span {
            font-size: 0.688rem;
            margin-top: 0.313rem;
            font-weight: normal;
        }
    }

    .container-sidebar svg {
        height: 1.6rem;
        width: 1.6rem;
    }

    .container-sidebar .btn-cta svg {
        fill: #fff;
    }

    .container-sidebar .flex-even {
        border-bottom: 1px solid var(--border-color);

        svg {
            height: 0.8rem;
            width: 0.8rem;
        }
    }
</style>
